<script setup lang="ts">
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Cid, Id, Post } from '@@/types'
import SurfacePopupWithTip from '@@/vuecomponents/SurfacePopupWithTip.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import Draggable from 'vuedraggable'

const props = withDefaults(
  defineProps<{
    postBeingEditedCid: Cid | null
    currentSortedPosts: Post[]
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const emit = defineEmits<{
  (name: 'dragging', dragging: boolean): void
}>()

const { user } = storeToRefs(useSurfaceStore())
const { canIModerate, canIWrite } = storeToRefs(useSurfacePermissionsStore())
const { shouldDisableDraggingPost } = storeToRefs(useSurfacePostsDragAndDropStore())
const { sortPost } = useSurfacePostsStore()

const postCidBeingDragged = ref<Cid | null>(null)
const dragging = ref<boolean>(false)
const dragCloneStyle = ref<HTMLStyleElement | undefined>()

const canDrag = computed<boolean>(() => {
  return !props.postBeingEditedCid && !shouldDisableDraggingPost.value
})

function shouldMatchApprovalColor(isAboveLine: boolean, isPublished?: boolean): boolean {
  if (isPublished) return false
  if (isAboveLine) return canIModerate.value
  return !canIModerate.value
}

function wasWrittenByCurrentUser(post: Post): boolean {
  return !!user.value.id && String(post.author_id) === String(user.value.id)
}

function isEditableByCurrentUser(post: Post): boolean {
  return canIModerate.value || (wasWrittenByCurrentUser(post) && canIWrite.value)
}

let mutationObserver: MutationObserver
function onStartDrag(e): void {
  dragging.value = true
  const dragNode = document.querySelector('.draggable-mirror')
  if (dragNode && dragCloneStyle.value) {
    const rect = dragNode.getBoundingClientRect()
    const middleX = rect.left + rect.width / 2
    // We use this to move the line and circle to the cursor
    // when user started dragging the post
    // 11 = post circle / 2 - we hardcode this so that browser don't need to
    // recalculate, which lags out the dragging
    const xOffsetFix = (e.originalEvent.clientX || e.originalEvent.pageX) - middleX - 11
    dragCloneStyle.value.innerHTML = `
        .draggable-mirror .wish-popup-marker .circle-marker,
        .draggable-mirror .wish-popup-marker .drag-vertical-line:before,
        .draggable-mirror .wish-popup-marker .drag-vertical-line:after {
          transform: translate(${xOffsetFix}px, 0px);
        }
      `
    mutationObserver = new MutationObserver((change) => {
      const transform = (change[0].target as HTMLElement).style.transform
      const matrix = transform.replace('matrix(', '').replace(')', '').split(',')
      const yOffsetFix = -matrix[matrix.length - 1]
      if (dragCloneStyle.value === undefined) return
      dragCloneStyle.value.innerHTML = `
          .draggable-mirror .wish-popup-marker .circle-marker,
          .draggable-mirror .wish-popup-marker .drag-vertical-line:before,
          .draggable-mirror .wish-popup-marker .drag-vertical-line:after {
            transform: translate(${xOffsetFix}px, ${yOffsetFix}px);
          }
        `
    })
    mutationObserver.observe(dragNode, { attributes: true, attributeFilter: ['style'] })
  }
}

// This class is added to cancel extra transition when the post is dropped.
function addDragJustEndClass(el: HTMLElement): void {
  el.classList.add('drag-just-end')
  setTimeout(() => {
    el.classList.remove('drag-just-end')
  }, 100)
}

// This is called when the user stops dragging the post
function onEndDrag(event: any): void {
  addDragJustEndClass(event.item)
  dragging.value = false
}

function onMove(e): void {
  // Do not trigger sorting post on move because it makes the timeline messy.
  // Reason is we allow real-time updates while dragging.

  // Remember the postCid that is being dragged
  const postCid = e.dragged.dataset.postCid
  postCidBeingDragged.value = postCid
}

function reorder(postCid: Cid, list: Post[]): void {
  const postOrderInCids = list.map((p) => p.cid)
  const postOrderInIds = list.map((p) => p.id)
  const postIndex = postOrderInCids.indexOf(postCid)
  const earlierPostId = postIndex === 0 ? null : postOrderInIds[postIndex - 1]
  const laterPostId = postIndex === list.length - 1 ? null : postOrderInIds[postIndex + 1]
  const sortIndex = computePostIndexFromPost(null, {
    prevId: laterPostId === null ? null : `wish-${laterPostId}`,
    nextId: earlierPostId === null ? null : `wish-${earlierPostId}`,
  }).sortIndex
  sortPost({
    postCid,
    sortIndex,
  })
}

function startLeaveAnimation(el: HTMLDivElement): void {
  el.style.left = el.dataset.left ?? ''
  el.style.top = el.dataset.top ?? ''
  el.style.position = 'absolute'

  const grandPa = el.parentElement?.parentElement
  el.parentElement?.removeChild(el)
  grandPa?.appendChild(el)
}

onMounted(() => {
  dragCloneStyle.value = document.createElement('style')
  document.head.appendChild(dragCloneStyle.value)
})

watch(dragging, () => {
  emit('dragging', dragging.value)
})

const draggableSortablePostArray = computed<Post[]>({
  get() {
    return [...props.currentSortedPosts]
  },
  set(updated: Post[]) {
    if (postCidBeingDragged.value != null) {
      reorder(postCidBeingDragged.value, updated)
      postCidBeingDragged.value = null
    }
  },
})
</script>

<template>
  <draggable
    ref="draggableWrapper"
    v-model="draggableSortablePostArray"
    class="wish-draggable-wrapper"
    :force-fallback="true"
    :delay="150"
    :delay-on-touch-only="true"
    handle=".wish-popup-marker, .wish-popup"
    :animation="250"
    :scroll-sensitivity="250"
    :filter="'.no-drag'"
    :prevent-on-filter="false"
    :disabled="!canDrag"
    :move="onMove"
    :invert-swap="true"
    drag-class="draggable-mirror"
    @start="onStartDrag"
    @end="onEndDrag"
  >
    <transition-group
      tag="div"
      class="flex h-full current-wishes"
      enter-class="opacity-0"
      enter-active-class="transform-opacity ease-out duration-500"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-active-class="transition-opacity ease-in duration-500"
      leave-to-class=" opacity-0"
      :duration="500"
      @before-leave="startLeaveAnimation"
    >
      <div
        v-for="(post, index) in draggableSortablePostArray"
        :key="post.cid"
        :data-post-cid="post.cid"
        :class="[
          'wish-wrapper',
          index % 2 ? 'below-line' : 'above-line',
          { 'no-drag': !isEditableByCurrentUser(post) || !canDrag },
        ]"
      >
        <div class="wish-popup-marker">
          <div class="circle-marker" />
          <div class="drag-vertical-line" />
        </div>
        <SurfacePopupWithTip
          :class="['wish-popup', index % 2 ? 'below-line' : 'above-line']"
          :color="post.color"
          :should-match-approval-color="shouldMatchApprovalColor(index % 2 === 0, post.published)"
        >
          <surface-post
            :index="index"
            :post="post"
            :put-attachment-on-top="true"
            :is-last="index === currentSortedPosts.length - 1"
            :x-shadow="false"
            :post-reaction-ids="reactionIdsByPostId[post.id]"
            :post-comment-ids="commentIdsByPostId[post.id]"
          />
        </SurfacePopupWithTip>
      </div>
    </transition-group>
  </draggable>
</template>
